import type { CollabEnvelope } from '../types/envelope.js';
import { validateEnvelope } from '../validation/envelope-validation.js';
import type { LinkAdapter } from '../types/transport.js';
import type { LinkKind } from '../types/routing.js';

export type WebSocketFactory = (url: string) => any;

export class WebSocketLinkAdapter implements LinkAdapter {
  readonly kind: LinkKind = 'tcp';
  readonly reliable = true;
  readonly mtu: number;

  private ws: any = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 10000;
  private reconnectTimeout: any = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 6;
  private isDestroyed = false;

  public onReceive: ((envelope: CollabEnvelope<unknown>) => void | Promise<void>) | null = null;

  constructor(
    private readonly url: string,
    onReceive?: (envelope: CollabEnvelope<unknown>) => void | Promise<void>,
    private readonly socketFactory?: WebSocketFactory,
    options?: { mtu?: number } | number
  ) {
    if (!url || url.trim() === '') {
      throw new Error('URL cannot be empty');
    }

    const mtuVal = typeof options === 'number' ? options : options?.mtu;
    if (mtuVal !== undefined && (!Number.isInteger(mtuVal) || mtuVal <= 0)) {
      throw new Error('MTU must be positive integer');
    }

    this.mtu = mtuVal ?? 65536;
    if (onReceive) {
      this.onReceive = onReceive;
    }
    this.connect();
  }

  private scheduleReconnect() {
    if (this.isDestroyed || this.reconnectTimeout) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[WebSocketLinkAdapter] Reached max reconnect attempts; pausing reconnects.');
      return;
    }

    const delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts), this.maxReconnectDelay);
    this.reconnectAttempts++;

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, delay);
  }

  private connect() {
    if (this.isDestroyed) return;

    // 1. Guard: Never attempt WS handshake if unauthenticated in browser
    let targetUrl = this.url;
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('token') 
        || localStorage.getItem('session_token') 
        || localStorage.getItem('guest_token') 
        || sessionStorage.getItem('token')
        || sessionStorage.getItem('session_token');

      if (!targetUrl.includes('token=') && !targetUrl.includes('guestToken=')) {
        if (!token) {
          // Wait until login or knock completion stores a valid token
          return;
        }
        const sep = targetUrl.includes('?') ? '&' : '?';
        targetUrl = `${targetUrl}${sep}token=${encodeURIComponent(token)}`;
      }
    }

    try {
      if (this.socketFactory) {
        this.ws = this.socketFactory(targetUrl);
      } else {
        if (typeof WebSocket === 'undefined') {
          throw new Error('WebSocket is not available');
        }
        this.ws = new WebSocket(targetUrl);
      }

      if (!this.ws) return;

      this.ws.onopen = () => {
        this.reconnectDelay = 1000;
        this.reconnectAttempts = 0;
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }
      };

      this.ws.onmessage = (event: { data: unknown }) => {
        if (this.isDestroyed) return;
        try {
          const raw = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (raw && typeof raw === 'object' && (raw as any).type === 'NODE_WELCOME') {
            return;
          }
          const envelope = validateEnvelope(raw);
          if (this.onReceive) {
            try {
              const res = this.onReceive(envelope);
              if (res && typeof (res as any).catch === 'function') {
                (res as any).catch(() => {});
              }
            } catch {}
          }
        } catch (err: any) {
          console.warn('[WebSocketLinkAdapter] Dropping invalid envelope:', err.message);
        }
      };

      this.ws.onclose = (event?: any) => {
        if (this.isDestroyed) return;
        // Do not flood on 401 / unauthorized / policy violation
        if (event && (event.code === 1008 || event.code === 4401)) {
          console.warn('[WebSocketLinkAdapter] Unauthorized; aborting reconnect until re-authenticated.');
          return;
        }
        this.scheduleReconnect();
      };

      this.ws.onerror = (err: unknown) => {
        console.warn('[WebSocketLinkAdapter] WebSocket error on', targetUrl, err);
      };
    } catch (err: any) {
      this.scheduleReconnect();
      throw err;
    }
  }

  isAvailable(): boolean {
    if (this.isDestroyed || !this.ws) return false;
    return this.ws.readyState === 1; // 1 = OPEN
  }

  async send(envelope: CollabEnvelope<unknown>): Promise<void> {
    if (this.isDestroyed || !this.isAvailable()) {
      throw new Error('WebSocketLink is closed or not connected');
    }
    const serialized = JSON.stringify(envelope);
    if (serialized.length > this.mtu) {
      throw new Error(`Envelope exceeds logical MTU of ${this.mtu}`);
    }
    this.ws.send(serialized);
  }

  close(): void {
    this.destroy();
  }

  destroy(): void {
    this.isDestroyed = true;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }
  }
}

export { WebSocketLinkAdapter as WebSocketLink };
